import { useEffect, useRef, useState } from "react";
import type { LayoutDoc } from "../../shared/layout";
import type { BedtimeSettings, ScreenSchedule } from "../../shared/models";
import { readPref, writePref } from "../data/persist";
import { isTyping, TYPING_HOLD_MS } from "../ui/keepTyping";

export type DayMode = "morning" | "day" | "night";

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h! * 60 + m!;
};

/** Which part of the day it is, per the household schedule. */
export function modeAt(minutes: number, s: ScreenSchedule): DayMode {
  const morning = toMinutes(s.morning.start);
  const day = toMinutes(s.day.start);
  const night = toMinutes(s.night.start);
  if (minutes >= morning && minutes < day) return "morning";
  if (minutes >= day && minutes < night) return "day";
  return "night";
}

/** Inside the household's bedtime hours, which usually run past midnight. */
export function inBedtime(minutes: number, b: BedtimeSettings): boolean {
  const start = toMinutes(b.start);
  const end = toMinutes(b.end);
  return start < end ? minutes >= start && minutes < end : minutes >= start || minutes < end;
}

export function localMinutes(now: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", minute: "numeric", hourCycle: "h23" }).formatToParts(now);
  return Number(parts.find((p) => p.type === "hour")?.value ?? 0) * 60 + Number(parts.find((p) => p.type === "minute")?.value ?? 0);
}

export function layoutForMode(mode: DayMode, s: ScreenSchedule, layouts: LayoutDoc[]): LayoutDoc | undefined {
  const key = s[mode].layoutKey;
  return layouts.find((l) => l.id === key) ?? layouts.find((l) => l.presetKey === key);
}

/**
 * Wall-display behaviour:
 * - switches to the scheduled screen when the day moves to morning / day / night,
 * - returns to it after `idleMinutes` without a touch,
 * - unless the current screen is pinned; pins clear at the morning switch,
 * - and never while someone is typing (a text box has focus).
 */
export function useScreenDirector(opts: {
  enabled: boolean;
  schedule: ScreenSchedule;
  idleMinutes: number;
  timeZone: string;
  layouts: LayoutDoc[];
  activeId: string | undefined;
  editing: boolean;
  /** Something more important holds the current screen (a severe-weather takeover). */
  hold?: boolean;
  select: (id: string) => void;
}) {
  const [pinned, setPinnedState] = useState<boolean>(() => readPref("pinned", false));
  const [mode, setMode] = useState<DayMode>(() => modeAt(localMinutes(new Date(), opts.timeZone), opts.schedule));
  const lastTouch = useRef(Date.now());
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const setPinned = (v: boolean) => {
    setPinnedState(v);
    writePref("pinned", v);
  };

  useEffect(() => {
    const touch = () => (lastTouch.current = Date.now());
    const evs = ["pointerdown", "keydown", "wheel"] as const;
    evs.forEach((e) => window.addEventListener(e, touch, { passive: true, capture: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, touch, { capture: true }));
  }, []);

  useEffect(() => {
    const tick = () => {
      const o = optsRef.current;
      // Never switch screens under someone typing; the switch happens once they're done.
      if (isTyping() && Date.now() - lastTouch.current < TYPING_HOLD_MS) return;
      const nextMode = modeAt(localMinutes(new Date(), o.timeZone), o.schedule);
      if (nextMode !== mode) {
        setMode(nextMode);
        // Overnight reset: a new morning always starts on the morning screen.
        const isPinned = nextMode === "morning" ? false : pinned;
        if (nextMode === "morning" && pinned) setPinned(false);
        if (o.enabled && !isPinned && !o.editing && !o.hold) {
          const target = layoutForMode(nextMode, o.schedule, o.layouts);
          if (target) o.select(target.id);
        }
        return;
      }
      if (!o.enabled || pinned || o.editing || o.hold || o.idleMinutes <= 0) return;
      if (Date.now() - lastTouch.current < o.idleMinutes * 60_000) return;
      const target = layoutForMode(mode, o.schedule, o.layouts);
      if (!target || target.id === o.activeId) return;
      // Close any open dialogs first (each listens for Escape), then switch.
      for (let i = 0; i < 5 && document.querySelector(".sheet"); i++) window.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
      o.select(target.id);
    };
    const t = window.setInterval(tick, 15_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, pinned]);

  return { mode, pinned, setPinned };
}
